/**
 * Promise-based dialog primitives that replace window.confirm /
 * window.prompt / window.alert with styled, brand-consistent modals.
 *
 * The shape matches the native APIs as closely as possible so a
 * replacement is a near-1:1 line swap:
 *
 *   if (window.confirm('Are you sure?')) { ... }
 *
 *   becomes
 *
 *   if (await confirmDialog({ title: 'Are you sure?' })) { ... }
 *
 * Wiring: a single <DialogHost /> mounted once at the App root
 * registers itself as the global dispatcher via `_registerDispatcher`.
 * All confirm/prompt/alert calls anywhere in the SPA route through
 * that one host — so dialogs render at the root of the React tree
 * (above modals, drawers, the rest), get a consistent backdrop, and
 * never interleave or stack incorrectly.
 *
 * If nothing has registered (a test, a story, an SSR pass), the
 * dialog helpers fall back to the native browser dialogs so callers
 * don't break.
 */

export interface ConfirmDialogOptions {
  title: string;
  /** Optional body text. Newlines render as paragraph breaks. */
  message?: string;
  /** Text on the primary button. Defaults to 'Confirm'. */
  confirmLabel?: string;
  /** Text on the secondary button. Defaults to 'Cancel'. */
  cancelLabel?: string;
  /** Render the primary button in clay (filled, attention-grabbing). */
  danger?: boolean;
}

export interface PromptDialogOptions extends ConfirmDialogOptions {
  /** Placeholder shown in the text input. */
  placeholder?: string;
  /** Initial input value (matches window.prompt's second arg). */
  defaultValue?: string;
  /** Render a multi-line textarea instead of a single-line input. */
  multiline?: boolean;
  /** Label rendered above the input. */
  inputLabel?: string;
  /**
   * If true, the OK button stays disabled until the input has
   * non-whitespace content. Use for required-input prompts.
   */
  requireValue?: boolean;
}

export interface AlertDialogOptions {
  title: string;
  message?: string;
  /** Text on the dismiss button. Defaults to 'OK'. */
  confirmLabel?: string;
}

type ResolvedDialog =
  | { kind: 'confirm'; opts: ConfirmDialogOptions; resolve: (value: boolean) => void }
  | { kind: 'prompt'; opts: PromptDialogOptions; resolve: (value: string | null) => void }
  | { kind: 'alert'; opts: AlertDialogOptions; resolve: () => void };

let dispatcher: ((dialog: ResolvedDialog) => void) | null = null;

/** Internal: <DialogHost> registers itself on mount. */
export function _registerDispatcher(fn: ((dialog: ResolvedDialog) => void) | null): void {
  dispatcher = fn;
}

export function confirmDialog(opts: ConfirmDialogOptions): Promise<boolean> {
  if (!dispatcher) {
    // SSR / unit-test fallback. window may not exist in jsdom test
    // environments but native confirm does — feature-detect.
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      return Promise.resolve(
        window.confirm([opts.title, opts.message].filter(Boolean).join('\n\n')),
      );
    }
    return Promise.resolve(false);
  }
  return new Promise<boolean>((resolve) => {
    dispatcher!({ kind: 'confirm', opts, resolve });
  });
}

export function promptDialog(opts: PromptDialogOptions): Promise<string | null> {
  if (!dispatcher) {
    if (typeof window !== 'undefined' && typeof window.prompt === 'function') {
      return Promise.resolve(
        window.prompt(
          [opts.title, opts.message].filter(Boolean).join('\n\n'),
          opts.defaultValue ?? '',
        ),
      );
    }
    return Promise.resolve(null);
  }
  return new Promise<string | null>((resolve) => {
    dispatcher!({ kind: 'prompt', opts, resolve });
  });
}

export function alertDialog(opts: AlertDialogOptions): Promise<void> {
  if (!dispatcher) {
    if (typeof window !== 'undefined' && typeof window.alert === 'function') {
      window.alert([opts.title, opts.message].filter(Boolean).join('\n\n'));
    }
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    dispatcher!({ kind: 'alert', opts, resolve });
  });
}

export type { ResolvedDialog };
